import React, { forwardRef, PropsWithChildren, useContext } from 'react'

import { ThemeContext } from 'styled-components'

import { useButton } from './Button.utils'

import {
  ButtonUI,
  LoadingWrapperUI,
  mapBrandColorToConfig,
  PrefixIconUI,
  ShortcutUI,
  SpinnerUI,
  SuffixIconUI,
} from './Button.styles'

import type { ButtonProps, ButtonThemeProp } from './Button.types'

export const WrappedButton = forwardRef<
  HTMLButtonElement,
  PropsWithChildren<ButtonProps>
>((props, ref) => {
  const {
    loading,
    children,
    prefixIcon,
    suffixIcon,
    shortcut,
    buttonRef,
    ...buttonProps
  } = useButton(props, ref)

  const { style = {} } = props
  const theme = useContext(ThemeContext)

  const styles = {
    ...style,
    ...mapBrandColorToConfig(theme?.brandColor as ButtonThemeProp),
  }

  const content = (
    <>
      {prefixIcon && <PrefixIconUI icon={prefixIcon} size="24" />}
      {children}
      {suffixIcon && <SuffixIconUI icon={suffixIcon} size="24" />}
      {shortcut && (
        <ShortcutUI className="c-Button--shortcut">{shortcut}</ShortcutUI>
      )}
    </>
  )

  return (
    <ButtonUI {...buttonProps} style={styles} ref={buttonRef}>
      {loading && (
        <>
          <SpinnerUI className="c-Button--spinner" size={12} />
          <LoadingWrapperUI>{content}</LoadingWrapperUI>
        </>
      )}
      {!loading && content}
    </ButtonUI>
  )
})

WrappedButton.defaultProps = {
  'data-cy': 'Button',
  disabled: false,
  linked: false,
  rounded: false,
  loading: false,
  size: 'lg',
  submit: false,
  color: 'blue',
}

WrappedButton.displayName = 'Button'

export default WrappedButton
